// マップシステム 型定義

import { Vector3D } from "../core/types";

// タイル定義
export interface TileDefinition {
  id: string; // ユニークID（文字列）
  name: string; // 表示名
  filename?: string; // GLTFファイル名
  offset?: Vector3D; // 位置オフセット
  size?: Vector3D; // サイズ
  scaling?: Vector3D; // スケール
  rotation?: Vector3D; // 回転（ラジアン）
  collides: boolean; // 衝突判定
  walkable: boolean; // 歩行可能か
  category: TileCategory; // タイルカテゴリ
  properties?: Record<string, unknown>; // 拡張プロパティ
}

// タイルカテゴリ
export enum TileCategory {
  GROUND = "ground", // 地面
  OBSTACLE = "obstacle", // 障害物
  DECORATION = "decoration", // 装飾
  INTERACTIVE = "interactive", // インタラクティブ（ボタン、スイッチ等）
  COLLECTIBLE = "collectible", // 収集アイテム
  GOAL = "goal", // ゴール
  HAZARD = "hazard", // 危険物
}

// マップ上のタイルインスタンス
export interface MapTile {
  id: string; // インスタンスID
  tileDefinitionId: string; // 参照するタイル定義ID
  position: Vector3D; // グリッド座標（x, y=layer, z）
  rotation?: Vector3D; // 追加回転
  state?: Record<string, unknown>; // 動的状態（スイッチON/OFF等）
}

// キャラクター定義
export interface CharacterDefinition {
  id: string;
  name: string;
  filename: string;
  offset: Vector3D;
  scaling: Vector3D;
}

// キャラクター配置
export interface CharacterPlacement {
  characterId: string; // キャラクター定義ID
  position: Vector3D; // 初期位置
  direction: Direction; // 初期向き
  isPlayer?: boolean; // プレイヤーキャラか
}

// 方向
export enum Direction {
  POSITIVE_X = 0, // +X方向
  POSITIVE_Z = 90, // +Z方向
  NEGATIVE_X = 180, // -X方向
  NEGATIVE_Z = 270, // -Z方向
}

// ゴール配置
export interface GoalPlacement {
  id: string;
  position: Vector3D;
  type: GoalType;
  conditions?: GoalCondition[];
}

// ゴールタイプ
export enum GoalType {
  REACH = "reach", // 到達
  COLLECT_ALL = "collect_all", // 全収集
  CUSTOM = "custom", // カスタム条件
}

// ゴール条件
export interface GoalCondition {
  type: string;
  params: Record<string, unknown>;
}

// マップ設定
export interface MapConfig {
  id: string;
  name: string;
  description?: string;
  tileSize: number; // タイルサイズ（ワールド座標）
  width: number; // マップ幅（タイル数）
  height: number; // マップ高さ（タイル数）
  layers: number; // レイヤー数
  tiles: MapTile[];
  characters: CharacterPlacement[];
  goals: GoalPlacement[];
  metadata?: MapMetadata;
}

// マップメタデータ
export interface MapMetadata {
  author?: string;
  createdAt?: string;
  updatedAt?: string;
  difficulty?: number;
  tags?: string[];
  thumbnail?: string;
}

// デフォルト値
export const DEFAULT_TILE_SIZE = 2;

export const DEFAULT_TILE_OFFSET: Vector3D = { x: 0, y: 0, z: 0 };
export const DEFAULT_TILE_SIZE_VEC: Vector3D = { x: 2, y: 1, z: 2 };
export const DEFAULT_TILE_SCALING: Vector3D = { x: 1, y: 1, z: 1 };
export const DEFAULT_TILE_ROTATION: Vector3D = { x: 0, y: 0, z: 0 };

// 方向を角度（ラジアン）に変換
export function directionToRadians(direction: Direction): number {
  switch (direction) {
    case Direction.POSITIVE_X:
      return -Math.PI / 2;
    case Direction.NEGATIVE_X:
      return Math.PI / 2;
    case Direction.POSITIVE_Z:
      return Math.PI;
    case Direction.NEGATIVE_Z:
      return 0;
    default:
      return 0;
  }
}

// 方向ベクトルを取得
export function directionToVector(direction: Direction): Vector3D {
  switch (direction) {
    case Direction.POSITIVE_X:
      return { x: 1, y: 0, z: 0 };
    case Direction.NEGATIVE_X:
      return { x: -1, y: 0, z: 0 };
    case Direction.POSITIVE_Z:
      return { x: 0, y: 0, z: 1 };
    case Direction.NEGATIVE_Z:
      return { x: 0, y: 0, z: -1 };
    default:
      return { x: 0, y: 0, z: 1 };
  }
}
